import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
  type SelectChangeEvent,
} from '@mui/material'
import { doc, onSnapshot, setDoc } from 'firebase/firestore'
import { db } from '../../firebase'

type VerificationMethod =
  | 'sms_otp'
  | 'missed_call'
  | 'missed_call_then_otp'
  | 'sms_otp_then_missed_call'

type AuthConfigDoc = {
  google_enabled: boolean
  phone_enabled: boolean
  otp_enabled: boolean
  verification_method: VerificationMethod
  auth: {
    phone_enabled: boolean
    google_enabled: boolean
    verification_method: VerificationMethod
  }
}

const WEBHOOK_URL =
  'https://us-central1-welamart-cef13.cloudfunctions.net/shernovaWebhook'

const METHOD_OPTIONS: Array<{ value: VerificationMethod; label: string; hint: string }> = [
  {
    value: 'missed_call',
    label: 'رنة فائتة فقط — Shernova',
    hint: 'التحقق برنة فائتة فقط دون تحويل لـ OTP.',
  },
  {
    value: 'sms_otp',
    label: 'OTP فقط — رسالة SMS',
    hint: 'التحقق برمز SMS فقط دون تحويل لرنة فائتة.',
  },
  {
    value: 'missed_call_then_otp',
    label: 'رنة فائتة ثم OTP عند الفشل',
    hint: 'يبدأ بالرنة، وإذا لم يصل التحقق يُسأل المستخدم عن التحويل إلى OTP.',
  },
  {
    value: 'sms_otp_then_missed_call',
    label: 'OTP ثم رنة فائتة عند الفشل',
    hint: 'يبدأ بـ OTP، وإذا لم يصل التحقق يُسأل المستخدم عن التحويل إلى رنة فائتة.',
  },
]

const defaults: AuthConfigDoc = {
  google_enabled: false,
  phone_enabled: true,
  otp_enabled: true,
  verification_method: 'sms_otp',
  auth: {
    phone_enabled: true,
    google_enabled: false,
    verification_method: 'sms_otp',
  },
}

function normalizeMethod(raw: unknown): VerificationMethod {
  if (
    raw === 'missed_call' ||
    raw === 'shernova' ||
    raw === 'missed_call_only'
  ) {
    return 'missed_call'
  }
  if (raw === 'missed_call_then_otp') return 'missed_call_then_otp'
  if (raw === 'sms_otp_then_missed_call') return 'sms_otp_then_missed_call'
  return 'sms_otp'
}

function normalizeFromFirestore(d: Record<string, unknown>): AuthConfigDoc {
  const nested = d.auth && typeof d.auth === 'object' ? (d.auth as Record<string, unknown>) : null
  const phone =
    typeof d.phone_enabled === 'boolean'
      ? d.phone_enabled
      : nested && typeof nested.phone_enabled === 'boolean'
        ? nested.phone_enabled
        : d.phone_enabled !== false && d.otp_enabled !== false
  const google = false
  const rawMethod = d.verification_method ?? nested?.verification_method
  const verification_method = normalizeMethod(rawMethod)
  return {
    google_enabled: google,
    phone_enabled: !!phone,
    otp_enabled: !!phone,
    verification_method,
    auth: {
      phone_enabled: !!phone,
      google_enabled: google,
      verification_method,
    },
  }
}

export default function AuthMethodsPage() {
  const [cfg, setCfg] = useState<AuthConfigDoc>(defaults)
  const [saving, setSaving] = useState(false)
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false,
    msg: '',
    sev: 'success',
  })

  useEffect(() => {
    const ref = doc(db, 'settings', 'auth_config')
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const d = snap.data() || {}
        setCfg(normalizeFromFirestore(d as Record<string, unknown>))
      },
      () => {
        setSnack({ open: true, msg: 'فشل تحميل إعدادات طرق الدخول', sev: 'error' })
      },
    )
    return () => unsub()
  }, [])

  const persist = async (next: Partial<AuthConfigDoc>) => {
    const merged: AuthConfigDoc = {
      ...cfg,
      ...next,
      auth: {
        ...cfg.auth,
        ...(next.auth || {}),
        phone_enabled: next.phone_enabled ?? next.auth?.phone_enabled ?? cfg.auth.phone_enabled,
        google_enabled: next.google_enabled ?? next.auth?.google_enabled ?? cfg.auth.google_enabled,
        verification_method:
          next.verification_method ?? next.auth?.verification_method ?? cfg.verification_method,
      },
    }
    merged.verification_method = merged.auth.verification_method
    merged.otp_enabled = merged.phone_enabled
    merged.auth.phone_enabled = merged.phone_enabled
    merged.auth.google_enabled = false
    merged.google_enabled = false

    setCfg(merged)
    setSaving(true)
    try {
      await setDoc(
        doc(db, 'settings', 'auth_config'),
        {
          google_enabled: merged.google_enabled,
          phone_enabled: merged.phone_enabled,
          otp_enabled: merged.otp_enabled,
          verification_method: merged.verification_method,
          auth: merged.auth,
          updatedAt: new Date(),
        },
        { merge: true },
      )
      setSnack({ open: true, msg: 'تم تحديث الإعدادات', sev: 'success' })
    } catch {
      setSnack({ open: true, msg: 'فشل تحديث الإعدادات', sev: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const onMethodChange = (e: SelectChangeEvent) => {
    const method = e.target.value as VerificationMethod
    persist({
      verification_method: method,
      auth: { ...cfg.auth, verification_method: method },
    })
  }

  const selectedHint =
    METHOD_OPTIONS.find((o) => o.value === cfg.verification_method)?.hint || ''
  const usesMissedCall =
    cfg.verification_method === 'missed_call' ||
    cfg.verification_method === 'missed_call_then_otp' ||
    cfg.verification_method === 'sms_otp_then_missed_call'

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        إدارة طرق الدخول
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography color="text.secondary">
              التغييرات تنعكس مباشرة على تطبيق KARSAL عبر stream من settings/auth_config.
              الدخول الاجتماعي (Google / Facebook / Apple) غير متاح — الدخول برقم الهاتف وكلمة المرور فقط.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={cfg.phone_enabled}
                  onChange={(_, checked) =>
                    persist({
                      phone_enabled: checked,
                      auth: { ...cfg.auth, phone_enabled: checked },
                    })
                  }
                  disabled={saving}
                />
              }
              label="تفعيل الدخول برقم الهاتف وكلمة المرور"
            />
            <FormControl fullWidth disabled={saving || !cfg.phone_enabled}>
              <InputLabel id="verification-method-label">طريقة التحقق من الهاتف</InputLabel>
              <Select
                labelId="verification-method-label"
                value={cfg.verification_method}
                label="طريقة التحقق من الهاتف"
                onChange={onMethodChange}
              >
                {METHOD_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedHint && <Alert severity="info">{selectedHint}</Alert>}
            {usesMissedCall && (
              <Alert severity="info">
                التحقق عبر Shernova SDK — verifyPhone + receipt على الخادم.
                Webhook URL (انسخه في لوحة Shernova):
                <TextField
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  value={WEBHOOK_URL}
                  InputProps={{ readOnly: true }}
                  onFocus={(e) => e.target.select()}
                />
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  )
}
